import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { Close, Delete, Edit, UploadFile } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { findProductById } from "../../../User/State/Product/Action";
import { createImage, deleteImage, updateImage } from "../../State/ProductImage/Action";
import ImagePopup from "../../../Common/ImagePopup";
import defaultImage from "../../../Common/productDefaultImage.png"

const ImageModal = ({ open, onClose, row, loading }) => {
  const { products, image } = useSelector((store) => store);
  const [editingId, setEditingId] = useState(null);
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState(null);
  const [images, setImages] = useState(products?.product?.images || []);
  const [color, setColor] = useState(null)
  const fileInputRef = React.useRef(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (row?.id) {
      dispatch(findProductById(row.id));
    }
  }, [dispatch, row?.id]);

  useEffect(() => {
    if (products?.product?.images) {
      setImages(products?.product?.images || []);
    }
  }, [products?.product]);


  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only JPG, JPEG, and PNG image are allowed!");
      setFile(null);
      setPreview(null);
      return;
    }

    // ✅ File size check (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      toast.error("Only less then 2MB size image are allowed!");
      setFile(null);
      setPreview(null);
      return;
    }

    // ✅ Quality check
    checkImageQuality(selectedFile);

  };

  const checkImageQuality = (file) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.width;
      const height = img.height;

      // ✅ Create canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // ✅ Convert to grayscale
      const gray = new Uint8ClampedArray(width * height);
      for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }

      // ✅ Compute Laplacian variance
      const kernel = [
        [0, 1, 0],
        [1, -4, 1],
        [0, 1, 0],
      ];

      const response = [];
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const pixel = gray[(y + ky) * width + (x + kx)];
              sum += kernel[ky + 1][kx + 1] * pixel;
            }
          }
          response.push(sum);
        }
      }

      const mean = response.reduce((a, b) => a + b, 0) / response.length;
      const variance =
        response.reduce((a, b) => a + (b - mean) ** 2, 0) / response.length;

      // ✅ Adjust threshold dynamically
      // Try starting from 20 → if still says blurry, reduce to 10 or 5
      if (variance < 50) {
        toast.error("Blur image are not allowed!");
        setFile(null);
        setPreview(null);
        return;
      }

      setFile(file);
      setPreview(URL.createObjectURL(file));

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    };
  };


  const refreshImages = () => {
    dispatch(findProductById(row?.id));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append("imageFile", file);

      if (color === null) {
        toast.error("Please select a color");
        return;
      }
      if (editingId) {
        dispatch(updateImage(editingId, isPrimary, color, formData)).then(refreshImages);
      } else {
        if (!file) {
          toast.error("Please select an image!");
          return;
        }
        dispatch(createImage(row?.id, isPrimary, color, formData)).then(refreshImages);
      }
      dispatch(findProductById(row?.id));
      resetForm();
    } catch (error) {
      toast.error("Failed to upload image" + error);
    }

  };

  const resetForm = () => {
    setPreview(null);
    setFile(null);
    setIsPrimary(false);
    setUploadedImage(null);
    setEditingId(null);
    setColor(null)

  };

  const handleImageUpdate = (productId, item) => {
    setUploadedImage(item.url || defaultImage);
    setColor(item?.colorId)
    setIsPrimary(item.isPrimary);
    setEditingId(item.id);
  };

  const handleDeleteClick = (event, id) => {
    if (event) event.stopPropagation();
    setDeleteImageId(id);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteImage(deleteImageId)).then(() => {
      dispatch(findProductById(row?.id));
    });
    setConfirmDialogOpen(false);
    resetForm();
    setDeleteImageId(null);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
    setDeleteImageId(null);
  };

  const handleModalClose = () => {
    resetForm(); // reset form state
    setImages([])
    onClose(); // call parent close
  };


  const rows = images?.map((image) => ({
    id: image?.id,
    productId: row?.id,
    color: image?.productColor?.color?.name || "N/A",
    colorId: image?.productColor?.id,
    url: image?.fullUrl,
    isPrimary: image?.isPrimary,
    product: row
  }))
    || [];

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "color", headerName: "Product Color", flex: 1 },
    {
      field: "url",
      headerName: "Image",
      flex: 1,
      renderCell: (params) => (
        <img onClick={() => { setImageOpen(true); setImageUrl(params?.value || defaultImage) }} className="cursor-pointer" src={params.value || defaultImage} alt="product" width="30" height="30" style={{ borderRadius: 8 }} />
      )
    },
    {
      field: "isPrimary",
      headerName: "Primary",
      flex: 1,
      renderCell: (params) => (params.value ? "YES" : "NO")
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) =>
        params?.row ? (
          <Box display="flex" gap={1} >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleImageUpdate(row?.id, params.row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                onClick={(event) => handleDeleteClick(event, params.row.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    }
  ];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "85%" : "70%",
            maxWidth: 800,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            maxHeight: "100vh",
            overflowY: "auto"
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Add Image</Typography>
            <IconButton onClick={handleModalClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Readonly Field */}
          <TextField
            label="Product Title"
            value={row?.title}
            name="title"
            size="small"
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={4}
            mb={2}
          >

            <FormControl fullWidth size="small">
              <InputLabel id="color-label">Product Color</InputLabel>
              <Select
                labelId="color-label"
                label="Product Color"
                id="colorId"
                name="colorId"
                value={color || ""}
                onChange={(e) => setColor(e.target.value)}
              >
                <MenuItem value={0} disabled>
                  Select Product color...
                </MenuItem>
                {products?.product?.colors?.map((color) => (
                  <MenuItem key={color.id} value={color.id}>
                    {color?.color?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                py: 1,
                fontSize: "0.8125rem", // matches MUI's small size
                textTransform: "none",  // optional: prevent uppercase if undesired
                minHeight: "36px",      // height for small buttons
              }}
              startIcon={<UploadFile fontSize="small" />} // smaller icon
            >
              Upload Image
              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={4}
            mb={2}
          >
            <FormControlLabel size="small"
              control={
                <Checkbox
                  size="small"
                  checked={isPrimary}
                  disabled={editingId}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                />
              }
              label="Primary Image"
            />
            {uploadedImage && (
              <div className="mt-2">
                <Typography>Current Image:</Typography>
                <img src={uploadedImage} alt="Uploaded" width="50" style={{ borderRadius: 8, mt: 2 }} />
              </div>
            )}
            {preview && (
              <div className="mt-2">
                <Typography>New Image Preview:</Typography>
                <img src={preview} alt="Preview" width="50" style={{ borderRadius: 8 }} />
              </div>
            )}
          </Box>
          {/* Centered Button */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#ef4444", color: "#fff" }}>
              {editingId ? "Update " : "Add "} Image
            </Button>
          </Box>

          {/* Data Grid */}
          <Box height={260}>
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 15, 20, 25]}
              disableRowSelectionOnClick
              loading={image?.loading || loading}
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </Box>

      </Modal>
      <Dialog open={confirmDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={cancelDelete}>Cancel</Button>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </>
  );
};

export default ImageModal;
